/**
 * Skeleton Loaders para carga inicial rápida
 * Estos componentes se muestran inmediatamente mientras cargan los datos
 */
import React from 'react';

// Skeleton base con animación
const Skeleton = ({ className = '', ...props }) => (
  <div 
    className={`animate-pulse bg-secondary/50 rounded ${className}`}
    {...props}
  />
);

// Skeleton para el header del dashboard
export const DashboardHeaderSkeleton = () => (
  <div className="mb-6 lg:mb-8 border-b border-border pb-6">
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
      <div>
        <Skeleton className="h-10 w-64 mb-2" />
        <Skeleton className="h-4 w-96" />
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-8 w-8 rounded-full" />
          <Skeleton className="h-8 w-8 rounded-full" />
          <Skeleton className="h-8 w-8 rounded-full" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-32 rounded-lg" />
          <Skeleton className="h-10 w-32 rounded-lg" />
        </div>
      </div>
    </div>
    {/* Search bar skeleton */}
    <div className="mt-6">
      <div className="flex gap-2 max-w-md">
        <Skeleton className="h-10 flex-1 rounded-sm" />
        <Skeleton className="h-10 w-20 rounded-sm" />
      </div>
    </div>
  </div>
);

// Skeleton para tarjetas de mercados
export const MarketCardSkeleton = () => (
  <div className="flex-shrink-0 w-48 p-4 bg-card border border-border rounded-xl">
    <div className="flex items-center justify-between mb-3">
      <Skeleton className="h-5 w-16" />
      <Skeleton className="h-4 w-8 rounded" />
    </div>
    <Skeleton className="h-3 w-24 mb-3" />
    <Skeleton className="h-6 w-20 mb-2" />
    <div className="flex items-center gap-2">
      <Skeleton className="h-4 w-12" />
      <Skeleton className="h-4 w-16" />
    </div>
  </div>
);

// Skeleton para la sección de mercados populares
export const MarketsListSkeleton = () => (
  <div className="bg-card border border-border rounded-xl p-5" data-testid="markets-skeleton">
    {/* Header */}
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-xl" />
        <div>
          <Skeleton className="h-5 w-40 mb-1" />
          <Skeleton className="h-3 w-32" />
        </div>
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-8 w-24 rounded-lg" />
        <Skeleton className="h-8 w-8 rounded-lg" />
      </div>
    </div>
    
    {/* Filtros de región */}
    <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
      {[1, 2, 3, 4, 5].map(i => (
        <Skeleton key={i} className="h-8 w-20 rounded-lg flex-shrink-0" />
      ))}
    </div>
    
    {/* Cards de mercados */}
    <div className="flex gap-4 overflow-x-auto pb-4">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <MarketCardSkeleton key={i} />
      ))}
    </div>
  </div>
);

// Skeleton para el gráfico principal
export const ChartSkeleton = () => (
  <div className="bg-card border border-border rounded-xl p-5" data-testid="chart-skeleton">
    {/* Header del stock */}
    <div className="mb-6 pb-4 border-b border-border">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-5 w-16" />
          </div>
          <Skeleton className="h-3 w-24" />
        </div>
        <div className="text-right">
          <Skeleton className="h-10 w-32 mb-2" />
          <Skeleton className="h-8 w-28 rounded-sm" />
        </div>
      </div>
    </div>
    
    {/* Period selector */}
    <div className="flex items-center gap-2 mb-4">
      <Skeleton className="h-4 w-16" />
      <div className="flex gap-1">
        {['1D', '5D', '1M', '3M', '6M', '1A', 'MAX'].map(p => (
          <Skeleton key={p} className="h-7 w-10 rounded-sm" />
        ))}
      </div>
    </div>
    
    {/* Chart area */}
    <div className="relative">
      <Skeleton className="h-[400px] w-full rounded-lg" />
      {/* Simular líneas del gráfico */}
      <div className="absolute inset-0 flex items-end justify-around px-8 pb-12">
        {[40, 60, 45, 70, 55, 80, 65, 75, 50, 85, 70, 60].map((h, i) => (
          <Skeleton 
            key={i} 
            className="w-4 rounded-t opacity-30" 
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
    </div>
    
    {/* Toolbar */}
    <div className="flex items-center gap-2 mt-4">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <Skeleton key={i} className="h-8 w-16 rounded-lg" />
      ))}
    </div>
  </div>
);

// Skeleton para indicadores técnicos
export const TechnicalIndicatorsSkeleton = () => (
  <div className="bg-card border border-border rounded-xl p-5" data-testid="indicators-skeleton">
    <div className="flex items-center gap-3 mb-4">
      <Skeleton className="h-10 w-10 rounded-xl" />
      <div>
        <Skeleton className="h-5 w-48 mb-1" />
        <Skeleton className="h-3 w-32" />
      </div>
    </div>
    
    {/* Grid de indicadores */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
        <div key={i} className="bg-secondary/30 rounded-lg p-4">
          <Skeleton className="h-4 w-16 mb-2" />
          <Skeleton className="h-6 w-20 mb-1" />
          <Skeleton className="h-3 w-24" />
        </div>
      ))}
    </div>
  </div>
);

// Skeleton para herramientas/calculadoras
export const ToolsRowSkeleton = () => (
  <div className="bg-card border border-border rounded-xl p-5" data-testid="tools-skeleton">
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-xl" />
        <div>
          <Skeleton className="h-5 w-32 mb-1" />
          <Skeleton className="h-3 w-48" />
        </div>
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-8 w-8 rounded-lg" />
        <Skeleton className="h-8 w-8 rounded-lg" />
      </div>
    </div>
    
    {/* Tools carousel */}
    <div className="flex gap-4 overflow-x-auto">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="flex-shrink-0 w-72 bg-secondary/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Skeleton className="h-6 w-6 rounded" />
            <Skeleton className="h-4 w-20" />
          </div>
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      ))}
    </div>
  </div>
);

// Skeleton para noticias
export const NewsSkeleton = () => (
  <div className="bg-card border border-border rounded-xl p-5" data-testid="news-skeleton">
    <div className="flex items-center gap-3 mb-4">
      <Skeleton className="h-10 w-10 rounded-xl" />
      <div>
        <Skeleton className="h-5 w-40 mb-1" />
        <Skeleton className="h-3 w-32" />
      </div>
    </div>
    
    <div className="space-y-4">
      {[1, 2, 3].map(i => (
        <div key={i} className="flex gap-4 p-4 bg-secondary/20 rounded-lg">
          <Skeleton className="h-20 w-20 rounded-lg flex-shrink-0" />
          <div className="flex-1">
            <Skeleton className="h-4 w-3/4 mb-2" />
            <Skeleton className="h-3 w-full mb-1" />
            <Skeleton className="h-3 w-2/3" />
            <div className="flex gap-2 mt-2">
              <Skeleton className="h-4 w-16 rounded" />
              <Skeleton className="h-4 w-20 rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Skeleton para portafolio
export const PortfolioSkeleton = () => (
  <div className="bg-card border border-border rounded-xl p-5" data-testid="portfolio-skeleton">
    <div className="flex items-center gap-3 mb-4">
      <Skeleton className="h-10 w-10 rounded-xl" />
      <div>
        <Skeleton className="h-5 w-36 mb-1" />
        <Skeleton className="h-3 w-48" />
      </div>
    </div>
    
    {/* Summary cards */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="bg-secondary/30 rounded-lg p-4">
          <Skeleton className="h-3 w-20 mb-2" />
          <Skeleton className="h-8 w-24" />
        </div>
      ))}
    </div>
    
    {/* Table skeleton */}
    <div className="space-y-2">
      <div className="flex gap-4 p-3 bg-secondary/20 rounded-lg">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-32 flex-1" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-24" />
      </div>
      {[1, 2, 3].map(i => (
        <div key={i} className="flex gap-4 p-3">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-32 flex-1" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-24" />
        </div>
      ))}
    </div>
  </div>
);

// Componente de pantalla de carga inicial completa
export const InitialLoadingSkeleton = () => (
  <div className="min-h-screen p-4 lg:p-6 animate-in fade-in duration-300">
    <DashboardHeaderSkeleton />
    <div className="space-y-4 lg:space-y-6">
      <MarketsListSkeleton />
      <ChartSkeleton />
      <TechnicalIndicatorsSkeleton />
    </div>
  </div>
);

// Zone Divider Skeleton
export const ZoneDividerSkeleton = () => (
  <div className="flex items-center gap-4 my-6">
    <Skeleton className="h-10 w-10 rounded-full" />
    <div>
      <Skeleton className="h-5 w-32 mb-1" />
      <Skeleton className="h-3 w-48" />
    </div>
    <Skeleton className="h-px flex-1" />
  </div>
);

export { Skeleton };
export default InitialLoadingSkeleton;
